import React from 'react';
import { useHistory } from 'react-router-dom';
import { HAPPY_PATH, RequestStatusDef, RequestStatusKey, STATUS_DEFS } from './statuses';

type ItemState = 'done' | 'current' | 'upcoming';

interface TimelineItem {
  def: RequestStatusDef;
  state: ItemState;
}

const markerIcon = (def: RequestStatusDef, state: ItemState) => {
  if (def.key === 'recusada') return 'close';
  if (def.key === 'ajuste') return 'priority_high';
  if (state === 'done' || (state === 'current' && def.key === 'concluida')) return 'check';
  if (state === 'current') return 'sync';
  return 'radio_button_unchecked';
};

const buildTimeline = (current: RequestStatusKey): TimelineItem[] => {
  if (current === 'ajuste' || current === 'recusada') {
    return [
      { def: STATUS_DEFS.enviada, state: 'done' },
      { def: STATUS_DEFS.em_analise, state: 'done' },
      { def: STATUS_DEFS[current], state: 'current' },
    ];
  }

  const currentIndex = HAPPY_PATH.indexOf(current);
  return HAPPY_PATH.map((key, index) => {
    let state: ItemState = 'upcoming';
    if (index < currentIndex) state = 'done';
    else if (index === currentIndex) state = 'current';

    if (key === 'aprovada' && state === 'upcoming') {
      return {
        def: {
          ...STATUS_DEFS.aprovada,
          label: 'Resultado da análise',
          description: 'A equipe vai conferir e informar o resultado.',
          tone: 'default' as const,
        },
        state,
      };
    }

    return { def: STATUS_DEFS[key], state };
  });
};

interface RequestStatusScreenProps {
  status: RequestStatusKey;
  reason: string | null;
  onBack: () => void;
  onEdit: () => void;
}

const RequestStatusScreen: React.FC<RequestStatusScreenProps> = ({
  status,
  reason,
  onBack,
  onEdit,
}) => {
  const history = useHistory();
  const timeline = buildTimeline(status);
  const showReason = status === 'ajuste' || status === 'recusada';
  const title = status === 'enviada' ? 'Solicitação enviada!' : 'Acompanhamento da solicitação';

  return (
    <div className="status-screen">
      <div className="text-center mb-4">
        <span className="material-icons status-screen-check" aria-hidden="true">
          check_circle
        </span>
        <h3 className="mt-2 mb-1">{title}</h3>
        <p className="text-muted mb-0">
          Acompanhe o andamento por aqui. A análise costuma levar de 1 a 2 dias úteis, e o status é
          atualizado automaticamente conforme a equipe avança.
        </p>
      </div>

      <ol className="status-timeline">
        {timeline.map((item) => (
          <li key={item.def.key} className={`status-item ${item.state} tone-${item.def.tone}`}>
            <span className="status-item-marker" aria-hidden="true">
              <span className="material-icons">{markerIcon(item.def, item.state)}</span>
            </span>
            <div className="status-item-body">
              <p className="status-item-label mb-1">{item.def.label}</p>
              <p className="status-item-desc mb-0">{item.def.description}</p>
            </div>
          </li>
        ))}
      </ol>

      {showReason && reason && (
        <div
          className={`alert mt-3 mb-0 ${status === 'recusada' ? 'alert-danger' : 'alert-warning'}`}
          role="status"
        >
          <p className="font-weight-bold mb-1">Motivo</p>
          <p className="mb-0">{reason}</p>
        </div>
      )}

      <div className="text-center mt-4">
        {status === 'concluida' && (
          <button
            type="button"
            className="btn btn-lg btn-submit d-block mx-auto mb-2"
            onClick={() => history.push('/conlicitacao_api/meus_tokens')}
          >
            Ver meu token
          </button>
        )}
        {status === 'ajuste' && (
          <button
            type="button"
            className="btn btn-lg btn-submit d-block mx-auto mb-2"
            onClick={onEdit}
          >
            Corrigir e reenviar
          </button>
        )}
        {status === 'recusada' && (
          <button
            type="button"
            className="btn btn-lg btn-submit d-block mx-auto mb-2"
            onClick={onEdit}
          >
            Fazer nova solicitação
          </button>
        )}
        <button type="button" className="btn btn-link" onClick={onBack}>
          Voltar para a página da API
        </button>
      </div>
    </div>
  );
};

export default RequestStatusScreen;
